import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Checkbox,
  Heading,
  IconCritical,
  Loader,
  Stack,
  Text,
} from 'braid-design-system';
import { useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import type {
  AccountActivationStatus,
  HirerAccountFragment,
  HirerAccountsQuery,
  HirerAccountsQueryVariables,
  PhysicalAddress,
} from 'src/App/generated/graphql-types';
import { HIRER_ACCOUNTS } from 'src/gql/queries/hirerAccounts';
import translations from 'src/pages/LinkYourSoftware/.vocab';

import type { Partner } from '../../../../../../../../types.ts';
import type { FormValues } from '../../../../../ContentState';

const MAX_ACCOUNTS_SELECTABLE = 10;

interface HirerSelectionProps {
  selectedPartner: Partner;
}

export const HirerSelection = ({ selectedPartner }: HirerSelectionProps) => {
  const { t } = useTranslations(translations);
  const [error, setError] = useState<string>();
  const [hirerAccounts, setHirerAccounts] = useState<HirerAccountFragment[]>();

  const { fields, replace } = useFieldArray<FormValues, 'hirerAccounts'>({
    name: 'hirerAccounts',
    rules: {
      validate: (value) => {
        if (value.filter(({ selected }) => selected).length === 0) {
          return t('selectAtLeastOneOption');
        }
        return true;
      },
    },
  });
  const { formState, clearErrors } = useFormContext<FormValues>();

  const hirerHasJobPostingAddress = (address: PhysicalAddress): boolean =>
    Boolean(address.addressLine1?.trim() && address.country?.trim());

  const displayErrorMessage = ({
    integrations,
    activationStatus,
    postalAddress,
  }: {
    activationStatus: AccountActivationStatus;
    integrations: HirerAccountFragment['integrations'];
    postalAddress: PhysicalAddress;
  }) => {
    if (activationStatus !== 'ACTIVE') {
      return t('accountNotActive');
    }

    if (!hirerHasJobPostingAddress(postalAddress)) {
      return t('Update your company address to link this account');
    }

    if (integrations.some(({ partner }) => partner.id === selectedPartner.id)) {
      const partnerName = selectedPartner.name;
      return t('accountAlreadyLinked', { partnerName });
    }

    if (integrations.length > 0) {
      const firstIntegrationPartnerName = integrations[0].partner.name;

      return t('accountLinkedWithOtherPartner', {
        firstIntegrationPartnerName,
      });
    }
  };

  const { loading } = useQuery<HirerAccountsQuery, HirerAccountsQueryVariables>(
    HIRER_ACCOUNTS,
    {
      onCompleted: ({ hirerAccounts: accounts }) => {
        setHirerAccounts(
          accounts.edges.flatMap((edge) =>
            !edge.node.isArchived ? [edge.node] : [],
          ),
        );
        replace(accounts.edges.map((edge) => edge.node));
      },
      onError: (err) => {
        datadogRum.addError(err, { err });
        setError(err.message);
      },
    },
  );

  const { hirerAccounts: watchedHirerAccounts } = useWatch<FormValues>();
  const totalSelected =
    watchedHirerAccounts?.filter(({ selected }) => selected).length || 0;

  const hirerAccountErrors = formState.errors.hirerAccounts;

  return (
    <Box id="lys-hirer-selection">
      <Stack space="gutter">
        <Heading level="3">{t('selectSeekAccountsTitle')}</Heading>
        <Text tone="secondary">{t('selectSeekAccounts')}</Text>
        <Stack space="medium">
          {error ? (
            <Alert tone="critical">
              <Text>{error}</Text>
            </Alert>
          ) : null}
          {loading && !hirerAccounts && <Loader />}
          {hirerAccounts?.length === 0 && (
            <Alert tone="info">
              <Text>{t('noLinkedEmployerAccounts')}</Text>
            </Alert>
          )}
          {hirerAccounts &&
            hirerAccounts.length > 0 &&
            fields.map(
              (
                {
                  businessName,
                  billingAccountId,
                  integrations,
                  activationStatus,
                  postalAddress,
                },
                index,
              ) => (
                <Controller
                  name={`hirerAccounts.${index}.selected`}
                  key={`hirerAccounts.${index}.selected`}
                  render={({ field }) => {
                    const description = displayErrorMessage({
                      integrations,
                      activationStatus,
                      postalAddress,
                    });

                    return (
                      <Checkbox
                        onChange={(e) => {
                          field.onChange(e);
                          clearErrors('hirerAccounts');
                        }}
                        checked={field.value}
                        tone={hirerAccountErrors && 'critical'}
                        label={`${businessName} (${
                          billingAccountId
                            ? parseInt(billingAccountId, 10)
                            : null
                        })`}
                        disabled={
                          activationStatus !== 'ACTIVE' ||
                          !hirerHasJobPostingAddress(postalAddress) ||
                          (totalSelected >= MAX_ACCOUNTS_SELECTABLE &&
                            !field.value)
                        }
                        description={description}
                        id={`checkbox-hirer-account-${index}`}
                        data={{ testid: `checkbox-hirer-account-${index}` }}
                      />
                    );
                  }}
                />
              ),
            )}
          {hirerAccountErrors ? (
            <Text
              id="hirer-accounts-message"
              icon={<IconCritical />}
              tone="critical"
              size="small"
            >
              {hirerAccountErrors.root?.message}
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};
